import Emittery from "emittery";
import superjson from "superjson";
import { hasLocalStorage } from "../shared/support";

export interface LocalStorageEvents<T> {
	added: {
		value: T;
		isCurrentTab: boolean;
	};
	removed: {
		value: T;
		isCurrentTab: boolean;
	};
}

export class LocalStorageSet<T> extends Emittery<LocalStorageEvents<T>> {
	private _values: Set<T>;
	constructor(
		private storageKey: string,
		values?: readonly T[] | null,
	) {
		super();
		if (!hasLocalStorage()) {
			this._values = new Set<T>(values);
			return;
		}
		const storedValue = localStorage.getItem(this.storageKey);
		if (storedValue) {
			this._values = superjson.parse<Set<T>>(storedValue);
			values?.forEach((v) => {
				this._values.add(v);
			});
		} else {
			this._values = new Set<T>(values);
		}
		window.addEventListener("storage", (e) => {
			if (e.key === this.storageKey && e.storageArea === localStorage) {
				const newValue = e.newValue;
				if (newValue) {
					const newSet = superjson.parse<Set<T>>(newValue);
					newSet.forEach((value) => {
						if (!this._values.has(value)) {
							void this.emit("added", {
								value,
								isCurrentTab: false,
							});
						}
					});
					this._values.forEach((value) => {
						if (!newSet.has(value)) {
							void this.emit("removed", {
								value,
								isCurrentTab: false,
							});
						}
					});
					this._values = newSet;
				} else {
					this._values.forEach((value) => {
						void this.emit("removed", {
							value,
							isCurrentTab: false,
						});
					});
					this._values.clear();
				}
			}
		});
	}

	get size() {
		return this._values.size;
	}

	add(value: T): this {
		this._values.add(value);
		void this.emit("added", { value, isCurrentTab: true });
		this.save();
		return this;
	}
	clear(): void {
		this._values.forEach((value) => {
			void this.emit("removed", { value, isCurrentTab: true });
		});
		this._values.clear();
		localStorage.removeItem(this.storageKey);
	}
	delete(value: T): boolean {
		const deleted = this._values.delete(value);
		if (deleted) {
			void this.emit("removed", { value, isCurrentTab: true });
			this.save();
		}
		return deleted;
	}
	forEach(
		callbackfn: (value: T, value2: T, set: Set<T>) => void,
		// biome-ignore lint/suspicious/noExplicitAny: Typescript lib has any for thisArg
		thisArg?: any,
	): void {
		return this._values.forEach(callbackfn, thisArg);
	}
	has(value: T): boolean {
		return this._values.has(value);
	}
	entries(): SetIterator<[T, T]> {
		return this._values.entries();
	}
	keys(): SetIterator<T> {
		return this._values.keys();
	}
	values(): SetIterator<T> {
		return this._values.values();
	}
	[Symbol.iterator](): SetIterator<T> {
		return this._values[Symbol.iterator]();
	}
	[Symbol.toStringTag] = "LocalStorageSet";

	private save() {
		const serializedValues = superjson.stringify(this._values);
		localStorage.setItem(this.storageKey, serializedValues);
	}
}

interface LocalStorageMapEvents<K, V> {
	added: {
		key: K;
		value: V;
		isCurrentTab: boolean;
	};
	removed: {
		key: K;
		value: V;
		isCurrentTab: boolean;
	};
	updated: {
		key: K;
		oldValue: V;
		newValue: V;
		isCurrentTab: boolean;
	};
}

export class LocalStorageMap<K, V> extends Emittery<
	LocalStorageMapEvents<K, V>
> {
	private _values: Map<K, V>;
	constructor(
		private storageKey: string,
		entries?: readonly (readonly [K, V])[] | null,
	) {
		super();
		if (!hasLocalStorage()) {
			this._values = new Map<K, V>(entries);
			return;
		}
		const storedValue = localStorage.getItem(this.storageKey);
		if (storedValue) {
			this._values = superjson.parse<Map<K, V>>(storedValue);
			entries?.forEach(([k, v]) => {
				this._values.set(k, v);
			});
		} else {
			this._values = new Map<K, V>(entries);
		}
		window.addEventListener("storage", (e) => {
			if (e.key === this.storageKey && e.storageArea === localStorage) {
				const newValue = e.newValue;
				if (newValue) {
					const newMap = superjson.parse<Map<K, V>>(newValue);
					newMap.forEach((value, key) => {
						const oldValue = this._values.get(key);
						if (oldValue !== value) {
							if (oldValue === undefined) {
								void this.emit("added", {
									key,
									value,
									isCurrentTab: false,
								});
							} else {
								void this.emit("updated", {
									key,
									oldValue,
									newValue: value,
									isCurrentTab: false,
								});
							}
						}
					});
					this._values.forEach((value, key) => {
						if (!newMap.has(key)) {
							void this.emit("removed", {
								key,
								value,
								isCurrentTab: false,
							});
						}
					});
					this._values = newMap;
				} else {
					this._values.forEach((value, key) => {
						void this.emit("removed", { key, value, isCurrentTab: false });
					});
					this._values.clear();
				}
			}
		});
	}
	clear(): void {
		this._values.forEach((value, key) => {
			void this.emit("removed", { key, value, isCurrentTab: true });
		});
		this._values.clear();
		localStorage.removeItem(this.storageKey);
	}
	delete(key: K): boolean {
		const old = this._values.get(key);
		if (!old) {
			return false;
		}
		this._values.delete(key);
		void this.emit("removed", { key, value: old, isCurrentTab: true });
		this.save();
		return true;
	}
	forEach(
		callbackfn: (value: V, key: K, map: Map<K, V>) => void,
		thisArg?: unknown,
	): void {
		return this._values.forEach(callbackfn, thisArg);
	}
	get(key: K): V | undefined {
		return this._values.get(key);
	}
	has(key: K): boolean {
		return this._values.has(key);
	}
	set(key: K, value: V): this {
		const old = this._values.get(key);
		this._values.set(key, value);
		if (old === undefined) {
			void this.emit("added", { key, value, isCurrentTab: true });
		} else {
			void this.emit("updated", {
				key,
				oldValue: old,
				newValue: value,
				isCurrentTab: true,
			});
		}
		this.save();
		return this;
	}
	entries(): MapIterator<[K, V]> {
		return this._values.entries();
	}
	keys(): MapIterator<K> {
		return this._values.keys();
	}
	values(): MapIterator<V> {
		return this._values.values();
	}
	[Symbol.iterator](): MapIterator<[K, V]> {
		return this._values[Symbol.iterator]();
	}
	[Symbol.toStringTag] = "LocalStorageMap";

	get size() {
		return this._values.size;
	}

	private save() {
		const serializedValues = superjson.stringify(this._values);
		localStorage.setItem(this.storageKey, serializedValues);
	}
}
